import axios from "axios"
import { LIFECYCLE_EMAIL_URL } from "utils/functions"

const STORAGE_KEY = "planhopper.registrationEmailPreference.v1"
const MAX_AGE = 86400
let inFlight = null

const read = () => {
  try {
    const pending = JSON.parse(sessionStorage.getItem(STORAGE_KEY))
    if (
      !pending ||
      typeof pending.lifecycle !== "boolean" ||
      !Number.isInteger(pending.started_at) ||
      Date.now() / 1000 - pending.started_at > MAX_AGE
    ) {
      clearRegistrationEmailPreference()
      return null
    }
    return pending
  } catch {
    return null
  }
}

const write = pending => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending))
  } catch {
    // An unavailable browser store must not prevent account creation.
  }
}

export const clearRegistrationEmailPreference = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {}
}

export const beginRegistrationEmailPreference = lifecycle => {
  clearRegistrationEmailPreference()
  if (!LIFECYCLE_EMAIL_URL || typeof lifecycle !== "boolean") return null
  try {
    const nonce = window.crypto.randomUUID()
    write({
      lifecycle,
      nonce,
      started_at: Math.floor(Date.now() / 1000),
      notice_version: "registration-notice-v1",
    })
    return nonce
  } catch {
    return null
  }
}

export const bindRegistrationEmailPreference = sub => {
  const pending = read()
  if (pending && sub) write({ ...pending, sub })
}

// Amplify emits this only after checking the Hosted UI OAuth state.
export const confirmRegistrationEmailOAuth = state => {
  const pending = read()
  if (!pending || state !== pending.nonce) return false
  write({ ...pending, oauthConfirmed: true })
  return true
}

export const syncRegistrationEmailPreference = user => {
  if (inFlight) return inFlight
  inFlight = (async () => {
    const pending = read()
    const sub = user?.attributes?.sub
    if (!pending || !sub || !LIFECYCLE_EMAIL_URL) return false
    if (pending.sub && pending.sub !== sub) {
      clearRegistrationEmailPreference()
      return false
    }
    if (!pending.sub && !pending.oauthConfirmed) return false
    bindRegistrationEmailPreference(sub)
    const token = user?.signInUserSession?.idToken?.jwtToken
    if (!token) return false
    // Signup workers are asynchronous. Retry briefly, then retain for next login.
    for (const delay of [0, 1000, 3000]) {
      if (delay) await new Promise(resolve => setTimeout(resolve, delay))
      if (read()?.nonce !== pending.nonce) return false
      try {
        await axios.post(
          `${LIFECYCLE_EMAIL_URL}/preferences/registration`,
          {
            lifecycle: pending.lifecycle,
            started_at: pending.started_at,
            ...(pending.notice_version
              ? { notice_version: pending.notice_version }
              : {}),
          },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 4000 },
        )
        if (read()?.nonce === pending.nonce) clearRegistrationEmailPreference()
        return true
      } catch (error) {
        const status = error.response?.status
        if (status && status < 500 && status !== 409 && status !== 429) {
          // Invalid/expired authentication can be retried after a fresh login.
          if (status !== 401 && status !== 403)
            clearRegistrationEmailPreference()
          return false
        }
      }
    }
    return false
  })()
    .catch(() => false)
    .finally(() => {
      inFlight = null
    })
  return inFlight
}
